import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Loader2, Calendar, MapPin, Clock, ExternalLink, Users, X, Globe } from "lucide-react";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eventTypes = ["all", "screening", "workshop", "meetup", "industry"];

// Parse date string as local date (not UTC) to prevent timezone shift
const parseLocalDate = (dateStr) => {
  if (!dateStr) return new Date();
  // If it's just a date string (YYYY-MM-DD), parse as local
  if (dateStr.length === 10 && dateStr.includes('-')) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  // Otherwise parse normally
  return new Date(dateStr);
};

// Format time to 12-hour AM/PM
const formatTime = (time, timezone) => {
  if (!time) return "";
  
  // Parse time (expected format: HH:MM or HH:MM:SS)
  const parts = time.split(':');
  if (parts.length < 2) return time; // Return as-is if not valid format
  
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  
  if (isNaN(hours) || isNaN(minutes)) return time; // Return as-is if can't parse
  
  const period = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  
  // Format timezone for display
  const tzAbbrev = timezone ? getTimezoneAbbrev(timezone) : '';
  
  return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}${tzAbbrev ? ` ${tzAbbrev}` : ''}`;
};

// Get timezone abbreviation
const getTimezoneAbbrev = (timezone) => {
  const abbrevMap = {
    "America/New_York": "ET",
    "America/Chicago": "CT",
    "America/Denver": "MT",
    "America/Los_Angeles": "PT",
    "America/Anchorage": "AKT",
    "Pacific/Honolulu": "HT",
    "Europe/London": "GMT",
    "Europe/Paris": "CET",
    "Europe/Berlin": "CET",
    "Europe/Moscow": "MSK",
    "Asia/Dubai": "GST",
    "Asia/Mumbai": "IST",
    "Asia/Singapore": "SGT",
    "Asia/Tokyo": "JST",
    "Asia/Shanghai": "CST",
    "Australia/Sydney": "AEST",
    "Pacific/Auckland": "NZST",
    "UTC": "UTC"
  };
  return abbrevMap[timezone] || timezone?.split('/').pop() || '';
};

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState("all");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [activeTab, setActiveTab] = useState("upcoming");

  useEffect(() => {
    fetchEvents();
  }, [selectedType]);

  const fetchEvents = async () => {
    try {
      const url = selectedType === "all" 
        ? `${API}/events` 
        : `${API}/events?event_type=${selectedType}`;
      const response = await axios.get(url);
      setEvents(response.data);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  // Check if event is past
  const isPastEvent = (event) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const eventDate = parseLocalDate(event.date);
    eventDate.setHours(23, 59, 59, 999);
    return eventDate < today;
  };

  // Split events into upcoming and past
  const upcomingEvents = events
    .filter(e => !isPastEvent(e))
    .sort((a, b) => parseLocalDate(a.date) - parseLocalDate(b.date));
  
  const pastEvents = events
    .filter(e => isPastEvent(e))
    .sort((a, b) => parseLocalDate(b.date) - parseLocalDate(a.date)); // Most recent first

  const displayedEvents = activeTab === "upcoming" ? upcomingEvents : pastEvents;
  const eventDates = events.map(e => parseLocalDate(e.date));

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      <SEO 
        title="Events" 
        description="Discover AI film events worldwide - screenings, workshops, and community meetups. Find events near you."
      />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-purple mb-4 font-mono">
            Calendar
          </p>
          <h1 className="font-syne text-4xl md:text-6xl font-bold text-white mb-6">
            Events
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl">
            Discover AI film and industry events worldwide.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Calendar Sidebar */}
          <div className="lg:col-span-1">
            <div className="p-6 bg-void-surface border border-white/5 sticky top-24">
              <h3 className="font-syne font-bold text-white mb-4">Calendar</h3>
              <CalendarComponent
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                className="rounded-none border-0"
                modifiers={{
                  hasEvent: eventDates
                }}
                modifiersStyles={{
                  hasEvent: { backgroundColor: 'rgba(147, 51, 234, 0.25)' }
                }}
              />
              
              {/* Event Type Filter */}
              <div className="mt-6">
                <h4 className="text-sm text-slate-500 mb-3">Filter by Type</h4>
                <div className="space-y-1">
                  {eventTypes.map((type) => (
                    <button
                      key={type}
                      onClick={() => setSelectedType(type)}
                      data-testid={`event-filter-${type}`}
                      className={`block w-full text-left px-3 py-2 text-sm transition-colors ${
                        selectedType === type
                          ? "bg-purple/20 text-purple-light"
                          : "text-slate-500 hover:text-white hover:bg-white/5"
                      }`}
                    >
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Events List */}
          <div className="lg:col-span-2">
            {/* Upcoming / Past Tabs */}
            <div className="flex gap-4 mb-8">
              <button
                onClick={() => setActiveTab("upcoming")}
                data-testid="tab-upcoming"
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === "upcoming"
                    ? "text-white border-b-2 border-purple"
                    : "text-slate-500 hover:text-white"
                }`}
              >
                Upcoming
                {upcomingEvents.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-purple/20 text-purple-light rounded-full">
                    {upcomingEvents.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab("past")}
                data-testid="tab-past"
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === "past"
                    ? "text-white border-b-2 border-purple"
                    : "text-slate-500 hover:text-white"
                }`}
              >
                Past
                {pastEvents.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-white/10 text-slate-400 rounded-full">
                    {pastEvents.length}
                  </span>
                )}
              </button>
            </div>

            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 text-purple animate-spin" />
              </div>
            ) : displayedEvents.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-slate-500">
                  {activeTab === "upcoming" ? "No upcoming events." : "No past events."}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {displayedEvents.map((event, index) => (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    data-testid={`event-${index}`}
                    onClick={() => setSelectedEvent(event)}
                    className={`p-6 bg-void-surface border border-white/5 hover:border-purple/20 transition-all duration-500 cursor-pointer ${
                      activeTab === "past" ? "opacity-75" : ""
                    }`}
                  >
                    <div className="flex flex-col md:flex-row md:items-start gap-6">
                      <div className="flex-shrink-0 text-center md:text-left">
                        <p className="text-3xl font-syne font-bold text-white">
                          {parseLocalDate(event.date).getDate()}
                        </p>
                        <p className="text-xs uppercase tracking-wider text-slate-500">
                          {parseLocalDate(event.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                        </p>
                      </div>
                      <div className="flex-grow">
                        <span className={`inline-block px-2 py-1 text-xs uppercase tracking-wider mb-3 ${
                          event.event_type === 'meetup' 
                            ? 'bg-purple/20 text-purple-light' 
                            : 'bg-white/5 text-slate-400'
                        }`}>
                          {event.event_type}
                        </span>
                        <h3 className="font-syne font-bold text-white text-xl mb-2">
                          {event.title}
                        </h3>
                        <p className="text-slate-500 mb-4 line-clamp-2 whitespace-pre-line">{event.description}</p>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatTime(event.time, event.timezone)}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {event.location}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Event Detail Modal */}
        <AnimatePresence>
          {selectedEvent && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
              onClick={() => setSelectedEvent(null)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-void-surface border border-white/10 p-8 max-w-lg w-full max-h-[80vh] overflow-y-auto"
              >
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <span className={`inline-block px-2 py-1 text-xs uppercase tracking-wider mb-3 ${
                      selectedEvent.event_type === 'meetup' 
                        ? 'bg-purple/20 text-purple-light' 
                        : 'bg-white/5 text-slate-400'
                    }`}>
                      {selectedEvent.event_type}
                    </span>
                    <h2 className="font-syne font-bold text-white text-2xl">
                      {selectedEvent.title}
                    </h2>
                  </div>
                  <button
                    onClick={() => setSelectedEvent(null)}
                    className="text-slate-400 hover:text-white transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-3 text-slate-300">
                    <Calendar className="w-5 h-5 text-purple-light" />
                    <span>{parseLocalDate(selectedEvent.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-300">
                    <Clock className="w-5 h-5 text-purple-light" />
                    <span>{formatTime(selectedEvent.time, selectedEvent.timezone)}</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-300">
                    <MapPin className="w-5 h-5 text-purple-light" />
                    <span>{selectedEvent.location}</span>
                  </div>
                  {selectedEvent.timezone && (
                    <div className="flex items-center gap-3 text-slate-300">
                      <Globe className="w-5 h-5 text-purple-light" />
                      <span>{selectedEvent.timezone.replace('_', ' ')}</span>
                    </div>
                  )}
                </div>

                <div className="mb-6">
                  <h3 className="text-sm font-medium text-slate-400 mb-2">Description</h3>
                  <p className="text-white whitespace-pre-line">{selectedEvent.description}</p>
                </div>

                {selectedEvent.registration_url && (
                  <a
                    href={selectedEvent.registration_url.startsWith('http') ? selectedEvent.registration_url : `https://${selectedEvent.registration_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-purple text-white font-bold uppercase tracking-widest text-xs hover:bg-purple-hover transition-all"
                  >
                    Register for Event <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
